/* Structural inputs — compatible with the @/data catalog objects (Vane/Point/
 * Nock) and any richer broadhead catalog. Only `name` is required; the rest
 * refine classification when present. Nothing here feeds ballistics. */
interface Vane { name: string; length?: string; weight?: number }
interface Point {
  name: string;
  model?: string;
  brand?: string;
  category?: string;
  bladeCount?: number;
  cutDiameterIn?: number;
  weight?: number;
}
interface Nock { name: string; lighted?: boolean; weight?: number }

/**
 * Arrow component profile library (v142+ Render mode).
 *
 * Maps every catalog entry to an accurate SILHOUETTE archetype and exposes
 * real-world dimensions, so the Render view can draw the user's actual build
 * true-to-life — the right vane shape at the right length, the right broadhead
 * with the right blade count and real cut diameter — all scaled to the arrow's
 * stated length. 100% our own vector geometry: no manufacturer photos, no IP
 * exposure, fast, and it carries straight into the share card.
 *
 * Nothing here feeds ballistics. Shapes + dimensions are cosmetic.
 */

export type VaneProfile =
  | 'shield'      // AAE Max Hunter — shield-cut, flat top, rear hook
  | 'parabolic'   // Bohning Blazer — short stiff dome, the NA best-seller
  | 'sabre'       // DCA Super Sabre — flat-top plateau, stepped shoulder, straight taper (2.25")
  | 'minisabre'   // DCA Mini Sabre — same stepped sabre silhouette, SHORTER + lower (2.0")
  | 'tac'         // TAC vane — rounded shoulder, convex taper
  | 'talon'       // AAE AIRAZR Talon — angular shoulder, dished taper
  | 'alpha'       // Alpha — low, long, sleek fin with a fine point
  | 'uv'          // UltraView UV — steep rear, short shoulder, long fine taper
  | 'lowprofile'  // Flex-Fletch FF-360 / target — long, low, smooth
  | 'highprofile' // AAE Max Stealth — tall full dome
  | 'airfoil'     // Firenock Aerovane 3D airfoil
  | 'spinwing'    // mylar spin wing / XS Wings — thin tall crescent
  | 'feather';    // natural feather (trad)

export type PointProfile =
  | 'field'        // conical bullet / field point
  | 'judo'         // small-game point with spring-wire arms
  | 'single-bevel' // long narrow single-bevel COC (Iron Will S, Cutthroat)
  | 'coc2'         // fixed 2-blade COC (Magnus Stinger, Slick Trick)
  | 'fixed3'       // fixed 3-blade (Muzzy Trocar, G5 Montec)
  | 'fixed4'       // fixed 4-blade (Iron Will Wide + bleeders)
  | 'hybrid-mech'  // hybrid mechanical (Sevr, Rage Hypodermic) — bleeder + deploy
  | 'full-mech';   // full mechanical (Rage 2-blade, Grim Reaper) — wide deploy

/** Parse an inch string like '2.1"' → 2.1 (falls back to `fallback`). */
export function parseInches(s: string | undefined, fallback: number): number {
  const v = parseFloat(String(s ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(v) && v > 0 ? v : fallback;
}

// ── Vane classification ────────────────────────────────────────────
// Ordered most-specific → least-specific. EVERY real catalog entry must land
// on a deliberate profile — the trailing `parabolic` is reserved for genuine
// Blazer-style stiff plastic vanes (Blazer, NAP QuikSpin), NOT a silent
// dumping-ground. The catalog→profile mapping is locked by an iterating test
// in arrowProfiles.test.ts, so any future unmapped entry fails the suite.
export function classifyVane(vane: Vane | undefined): VaneProfile {
  const n = (vane?.name ?? '').toLowerCase();

  // Mylar curl / spin-wing target vanes (Beiter, XS Wings, Speedflex, Kurly).
  if (/spin\s?wing|spinwing|xs wing|\bwings\b|speedflex|beiter|kurly/.test(n)) return 'spinwing';

  // Natural feathers. Brands that ONLY make feathers (Trueflight, Gateway,
  // Rayzr) route here even when the model name says "Shield" or "Parabolic" —
  // those words describe the feather CUT, not a plastic-vane silhouette.
  if (/feather|trad|trueflight|rayzr|gateway/.test(n)) return 'feather';

  // Firenock 3D airfoil family.
  if (/aero|aerovane|aeroweave/.test(n)) return 'airfoil';

  // Named single-reference silhouettes.
  if (/ultraview|\buv\b/.test(n)) return 'uv';
  if (/alpha/.test(n)) return 'alpha';            // before TAC — "TAC Alpha" is alpha
  if (/\btac\b/.test(n)) return 'tac';
  if (/talon|airazr/.test(n)) return 'talon';     // whole AAE AIRAZR family is the talon shape

  // DCA Sabre family — Mini MUST be checked before plain Super Sabre so a
  // "Mini Sabre" never collapses onto the full-size sabre profile.
  if (/mini\s*(?:super\s*)?sab[er]+|mini\s*sabre/.test(n)) return 'minisabre';
  if (/sabre|saber|\bdca\b/.test(n)) return 'sabre';

  // Tall high-profile fins.
  if (/\bhp\b|stealth|pro max/.test(n)) return 'highprofile';

  // Shield-cut hunting vanes.
  if (/shield|max hunter|hybrid|hunter|killer|heat|hex|bone\s?collector/.test(n)) return 'shield';

  // Low-profile target / hybrid plastic vanes.
  if (/x vane|x3|\bep-?\d|plastifletch|fusion|\btec\b|v-?max|driver|diamond|super spine|griff|vector|swift|snipe|rapt|air vane|f vane|silent knight|ffp|quad|360|flex-?fletch|\bmax\b|4 fletch|four fletch|spider|cx vane/.test(n))
    return 'lowprofile';

  // Genuine Blazer-style stiff parabolic (Blazer, NAP QuikSpin).
  return 'parabolic';
}

export function classifyPoint(point: Point | undefined): PointProfile {
  if (!point) return 'field';
  const n = `${point.name} ${point.model ?? ''} ${point.brand ?? ''}`.toLowerCase();
  if (point.category === 'field') {
    if (/judo|small.?game|bludgeon|blunt|grabbing/.test(n)) return 'judo';
    return 'field';
  }
  if (point.category === 'mechanical-broadhead') {
    if (/sevr|hypodermic|spitfire|hybrid|bleeder/.test(n)) return 'hybrid-mech';
    return 'full-mech';
  }
  // fixed-broadhead
  const blades = point.bladeCount;
  if (blades === 3) return 'fixed3';
  if (blades && blades >= 4) return 'fixed4';
  if (/single.?bevel|iron will s|s-series|s series|cutthroat|german kinetic|silver flame|grizzlystik|maasai|\bvpa\b|tuffhead|tanto/.test(n))
    return 'single-bevel';
  if (blades === 2) return 'coc2';
  return 'fixed3';
}

/** Real-world vane height (inches) by profile — used for true-scale rendering. */
export const VANE_HEIGHT_IN: Record<VaneProfile, number> = {
  shield: 0.55,      // Max Hunter — tall
  parabolic: 0.57,   // Blazer — short but tall-profile
  sabre: 0.55,       // DCA Super Sabre — 0.55" tall (verified dcacustomarrows.com)
  minisabre: 0.465,  // DCA Mini Sabre — 0.465" tall (verified) — shorter + lower than Super
  tac: 0.48,         // TAC vane — medium
  talon: 0.46,       // AIRAZR Talon — low-medium
  alpha: 0.40,       // Alpha — low, sleek
  uv: 0.46,          // UltraView UV — low-medium
  lowprofile: 0.40,  // FF-360 — low
  highprofile: 0.50, // Max Stealth — medium
  airfoil: 0.40,
  spinwing: 0.58,
  feather: 0.58,
};

/** Real-world head length (inches) tip-to-shoulder by profile. */
export const POINT_LENGTH_IN: Record<PointProfile, number> = {
  field: 0.85,
  judo: 0.70,
  'single-bevel': 1.45, // long, narrow
  coc2: 1.15,
  fixed3: 1.10,
  fixed4: 1.10,
  'hybrid-mech': 1.35,
  'full-mech': 1.30,
};

/** Default cut diameter (inches) when the catalog entry doesn't specify. */
const DEFAULT_CUT_IN: Record<PointProfile, number> = {
  field: 0,
  judo: 0,
  'single-bevel': 1.0,
  coc2: 1.125,
  fixed3: 1.125,
  fixed4: 1.0,
  'hybrid-mech': 1.6,
  'full-mech': 2.0,
};

export function pointCutDiameterIn(point: Point | undefined, profile: PointProfile): number {
  return point?.cutDiameterIn ?? DEFAULT_CUT_IN[profile];
}

// ── Vane silhouette path generators ────────────────────────────────
// All take pixel coords. (xR = rear/nock-side x, xF = front x, yBase = shaft
// surface on the vane's side, h = height px, s = +1 top / -1 bottom.)
// Each returns a closed SVG path `d` string for one vane.

// Each profile as fractional path commands: x in [0,1] across the vane length
// (0 = trailing/nock edge, 1 = leading/point edge), y in height-fractions
// (0 = base on shaft, 1 = peak). One serializer maps these to pixels and can
// bake a rotation in — so the splayed hens carry no SVG transform and render
// identically in every engine.
type VaneCmd =
  | ['M' | 'L', number, number]
  | ['Q', number, number, number, number]
  | ['C', number, number, number, number, number, number];

// Modeled from real product silhouettes (verified 2026-06). x: 0 = TRAILING
// (nock) edge, 1 = LEADING (point) edge. The modern vane family is a "fin": a
// tall ROUNDED SHOULDER at the rear (x≈0.15) that sweeps forward to a FINE
// SHARP POINT at the front (x=1). Profiles differ by height + top-edge curve
// (convex / straight / concave) — length comes from the catalog at true scale.
const PROFILE_CMDS: Record<VaneProfile, VaneCmd[]> = {
  // AAE Max Hunter / AAE MAX shield cut — tall rounded rear shoulder, long
  // near-straight (slightly dished) top to a sharp point. Modeled from the
  // product photos (Sal, 2026-06-06).
  shield: [['M', 0, 0], ['C', 0, 0.84, 0.04, 1, 0.14, 1], ['C', 0.46, 0.66, 0.8, 0.3, 1, 0]],
  // Bohning Blazer — short stiff fin, rounded shoulder ~1/5 back, convex top
  // to a sharp point. Modeled from the product photo (Sal, 2026-06-06).
  parabolic: [['M', 0, 0], ['C', 0, 0.82, 0.07, 1, 0.20, 1], ['C', 0.48, 0.95, 0.75, 0.48, 1, 0]],
  // DCA Super Sabre — straight forward-leaning rear edge to a FLAT-TOP rear
  // plateau, a stepped shoulder, then a long straight taper to a fine point.
  // Modeled directly from the product photo (Sal, 2026-06-06). All straight.
  sabre: [['M', 0, 0], ['L', 0.09, 1], ['L', 0.39, 1], ['L', 0.49, 0.58], ['L', 1, 0]],
  // DCA Mini Sabre — the SAME flat-top plateau / stepped shoulder / straight
  // taper as the Super Sabre, but a stubbier, more compact silhouette: shorter
  // rear plateau and an earlier step, so even at an identical footprint the
  // outline reads distinctly shorter than the full-size sabre. Its true 2.0"
  // length + lower height come through from the catalog at render time.
  minisabre: [['M', 0, 0], ['L', 0.11, 1], ['L', 0.33, 1], ['L', 0.43, 0.6], ['L', 1, 0]],
  // TAC vane — steep rounded rear shoulder, clean slightly-convex taper to a
  // fine sharp point. Modeled from the product photo (Sal, 2026-06-06).
  tac: [['M', 0, 0], ['C', 0, 0.84, 0.05, 1, 0.13, 1], ['C', 0.4, 0.85, 0.75, 0.4, 1, 0]],
  // AAE AIRAZR Talon 3.0 — angular rear shoulder, near-straight dished taper to
  // a long fine point. Modeled from the product photo (Sal, 2026-06-06).
  talon: [['M', 0, 0], ['C', 0, 0.85, 0.04, 1, 0.13, 1], ['C', 0.46, 0.58, 0.78, 0.26, 1, 0]],
  // Alpha — low, long, sleek fin: rounded shoulder, gentle convex taper to a
  // very fine point. Modeled from the product photo (Sal, 2026-06-06).
  alpha: [['M', 0, 0], ['C', 0, 0.82, 0.05, 1, 0.13, 1], ['C', 0.46, 0.66, 0.82, 0.3, 1, 0]],
  // UltraView UV — steep straight rear edge to a short high shoulder, then one
  // long straight taper to a very fine point. Modeled from the product photo.
  uv: [['M', 0, 0], ['L', 0.07, 1], ['L', 0.14, 0.92], ['L', 1, 0]],
  // AAE Max Stealth — longer, medium-height fin, gentle convex top.
  highprofile: [['M', 0, 0], ['C', 0, 0.8, 0.05, 1, 0.15, 1], ['C', 0.46, 0.9, 0.78, 0.4, 1, 0]],
  // Flex-Fletch FF-360 / target — long, low, near-straight fin.
  lowprofile: [['M', 0, 0], ['C', 0, 0.8, 0.05, 1, 0.15, 1], ['C', 0.5, 0.6, 0.78, 0.3, 1, 0]],
  // Firenock Aerovane II/III — smooth 3D airfoil dome, rounded (no sharp fin
  // point), peak biased forward. Modeled from the product photo (Sal).
  airfoil: [['M', 0, 0], ['C', 0.16, 0.5, 0.38, 1, 0.56, 1], ['C', 0.72, 1, 0.88, 0.55, 1, 0]],
  // Mylar spin wing — razor-thin swept crescent.
  spinwing: [['M', 0, 0], ['C', 0.24, 1.3, 0.7, 1.3, 1, 0.2], ['C', 0.7, 0.95, 0.3, 0.95, 0, 0]],
  // Natural feather — tall fin, fuller convex top, slightly rounded front.
  feather: [['M', 0, 0], ['C', 0, 0.85, 0.06, 1, 0.18, 1], ['C', 0.5, 1, 0.82, 0.62, 1, 0.08], ['L', 1, 0]],
};

interface VaneEmit {
  xR: number;
  xF: number;
  yBase: number;
  h: number;
  s: number;
  /**
   * Horizontal lean (px) applied at full height, scaled by height-fraction so
   * the BASE (fy=0) stays glued to the shaft and only the body leans. Used for
   * the splayed hen vanes — keeps them attached instead of peeling off.
   */
  skew?: number;
}

function emitVane(cmds: VaneCmd[], o: VaneEmit): string {
  const W = o.xF - o.xR;
  const skew = o.skew ?? 0;
  const map = (fx: number, fy: number): string => {
    const X = o.xR + W * fx + skew * fy;
    const Y = o.yBase - o.s * o.h * fy;
    return `${X.toFixed(2)} ${Y.toFixed(2)}`;
  };
  return cmds
    .map((c): string => {
      switch (c[0]) {
        case 'M':
        case 'L':
          return `${c[0]} ${map(c[1], c[2])}`;
        case 'Q':
          return `Q ${map(c[1], c[2])} ${map(c[3], c[4])}`;
        case 'C':
          return `C ${map(c[1], c[2])} ${map(c[3], c[4])} ${map(c[5], c[6])}`;
      }
    })
    .join(' ') + ' Z';
}

export function vanePath(profile: VaneProfile, xR: number, xF: number, yBase: number, h: number, s: number): string {
  return emitVane(PROFILE_CMDS[profile], { xR, xF, yBase, h, s });
}

// ── Realistic fletch geometry ──────────────────────────────────────
// A real fletch from the side isn't a symmetric lens: the cock vane stands
// tall and the hen vanes splay below, foreshortened. This returns the ordered
// (back-to-front) set of vane paths so the renderer can paint far→near→cock.

export interface FletchPath {
  d: string;
  kind: 'cock' | 'hen-near' | 'hen-far';
}

export function fletchPaths(
  profile: VaneProfile,
  vRear: number,
  vFront: number,
  centerY: number,
  shaftHalf: number,
  vaneH: number,
  vaneCount: number,
): FletchPath[] {
  const topBase = centerY - shaftHalf;
  const botBase = centerY + shaftHalf;
  const cmds = PROFILE_CMDS[profile];
  // Hens share the cock's base footprint on the shaft (vRear→vFront) and lean
  // via skew, so their base stays glued to the shaft — no peeling off.
  const hen = (h: number, skew: number, kind: FletchPath['kind']): FletchPath => ({
    kind,
    d: emitVane(cmds, { xR: vRear, xF: vFront, yBase: botBase, h, s: -1, skew }),
  });
  const cock: FletchPath = { kind: 'cock', d: vanePath(profile, vRear, vFront, topBase, vaneH, 1) };
  const span = vFront - vRear;

  if (vaneCount >= 4) {
    // 4-fletch: cock up + one hen straight down (the side pair reads edge-on).
    return [hen(vaneH * 0.9, 0, 'hen-near'), cock];
  }
  // 3-fletch: cock up + two hens splayed below. Far hen leans back (shorter,
  // darker, behind); near hen leans forward — a clean V, both bases attached.
  return [hen(vaneH * 0.6, -span * 0.16, 'hen-far'), hen(vaneH * 0.86, span * 0.16, 'hen-near'), cock];
}

// ── Point / broadhead silhouette generators ────────────────────────
// Returns an array of {d, kind} paths so the renderer can fill ferrule vs
// blade differently. xBase = shaft-front x, yC = centerline, tipX = point tip,
// rShaft = shaft half-thickness px, halfSpread = blade half-spread px.

export interface PointShape {
  d: string;
  /** 'wire' = stroke-only (judo arms); others are filled. */
  kind: 'ferrule' | 'blade' | 'bullet' | 'bleeder' | 'wire';
}

export function pointShapes(
  profile: PointProfile,
  xBase: number,
  tipX: number,
  yC: number,
  rShaft: number,
  halfSpread: number,
): PointShape[] {
  const W = tipX - xBase;
  const fer = Math.max(rShaft * 0.7, 2);

  if (profile === 'field') {
    const xSh = xBase + W * 0.45;
    return [{
      kind: 'bullet',
      d: `M ${xBase} ${yC - fer} L ${xSh} ${yC - fer} Q ${tipX} ${yC - fer * 0.5} ${tipX} ${yC} `
        + `Q ${tipX} ${yC + fer * 0.5} ${xSh} ${yC + fer} L ${xBase} ${yC + fer} Z`,
    }];
  }

  if (profile === 'judo') {
    // Short bullet body + spring-wire arms radiating out near the base.
    const xSh = xBase + W * 0.5;
    const body: PointShape = {
      kind: 'bullet',
      d: `M ${xBase} ${yC - fer} L ${xSh} ${yC - fer} Q ${tipX} ${yC - fer * 0.5} ${tipX} ${yC} `
        + `Q ${tipX} ${yC + fer * 0.5} ${xSh} ${yC + fer} L ${xBase} ${yC + fer} Z`,
    };
    const arm = (s: number, ax: number): PointShape => ({
      kind: 'wire',
      d: `M ${xBase + W * 0.1} ${yC + s * fer} L ${ax} ${yC + s * (fer + halfSpread || fer + W * 0.6)}`,
    });
    const tip = (fer + (halfSpread || W * 0.55));
    return [
      body,
      arm(-1, xBase - W * 0.1), arm(-1, xBase + W * 0.25), arm(-1, xBase + W * 0.55),
      arm(1, xBase - W * 0.1), arm(1, xBase + W * 0.25), arm(1, xBase + W * 0.55),
      // little balls at arm tips suggested via short forward hooks
      { kind: 'wire', d: `M ${xBase + W * 0.55} ${yC - tip} L ${xBase + W * 0.7} ${yC - tip}` },
      { kind: 'wire', d: `M ${xBase + W * 0.55} ${yC + tip} L ${xBase + W * 0.7} ${yC + tip}` },
    ];
  }

  const ferruleSpine: PointShape = {
    kind: 'ferrule',
    d: `M ${xBase} ${yC - fer} L ${tipX} ${yC} L ${xBase} ${yC + fer} Z`,
  };

  if (profile === 'single-bevel') {
    // Long, narrow knife-edge triangle, single continuous bevel to a fine tip.
    const sp = halfSpread;
    return [
      { kind: 'blade', d: `M ${tipX} ${yC} L ${xBase} ${yC - sp} L ${xBase - W * 0.04} ${yC} L ${xBase} ${yC + sp} Z` },
      ferruleSpine,
    ];
  }

  if (profile === 'coc2') {
    // Wider 2-blade COC with squared/stepped rear shoulders + ferrule spine.
    const sp = halfSpread;
    const blade = (s: number): PointShape => ({
      kind: 'blade',
      d: `M ${tipX} ${yC} L ${xBase + W * 0.06} ${yC - s * sp} L ${xBase} ${yC - s * sp * 0.78} `
        + `L ${xBase + W * 0.08} ${yC - s * sp * 0.62} L ${xBase} ${yC - s * fer} Z`,
    });
    return [blade(1), blade(-1), ferruleSpine];
  }

  if (profile === 'hybrid-mech' || profile === 'full-mech') {
    // Slim bullet ferrule + rear-deployed blades swept back-and-out.
    const xSh = xBase + W * 0.5;
    const pivotX = xBase + W * 0.6;
    const ferrule: PointShape = {
      kind: 'ferrule',
      d: `M ${xBase} ${yC - fer} L ${xSh} ${yC - fer} Q ${tipX} ${yC - fer * 0.4} ${tipX} ${yC} `
        + `Q ${tipX} ${yC + fer * 0.4} ${xSh} ${yC + fer} L ${xBase} ${yC + fer} Z`,
    };
    const blade = (s: number): PointShape => ({
      kind: 'blade',
      d: `M ${pivotX} ${yC - s * fer} L ${xBase - W * 0.12} ${yC - s * halfSpread} L ${xBase + W * 0.02} ${yC - s * fer} Z`,
    });
    const shapes: PointShape[] = [blade(1), blade(-1), ferrule];
    if (profile === 'hybrid-mech') {
      // small fixed bleeder near the tip
      const bx = xBase + W * 0.66;
      shapes.push(
        { kind: 'bleeder', d: `M ${bx} ${yC} L ${bx - W * 0.04} ${yC - fer * 2.2} L ${bx + W * 0.1} ${yC} Z` },
        { kind: 'bleeder', d: `M ${bx} ${yC} L ${bx - W * 0.04} ${yC + fer * 2.2} L ${bx + W * 0.1} ${yC} Z` },
      );
    }
    return shapes;
  }

  // Fixed 3/4-blade — central ferrule edge + swept blades widest at the rear.
  const sweptBlade = (s: number, spread: number): PointShape => ({
    kind: 'blade',
    d: `M ${tipX} ${yC} L ${xBase} ${yC - s * spread} L ${xBase + W * 0.22} ${yC - s * fer * 1.15} Z`,
  });
  if (profile === 'fixed4') {
    return [
      sweptBlade(1, halfSpread),
      sweptBlade(-1, halfSpread),
      { kind: 'bleeder', d: `M ${xBase + W * 0.35} ${yC} L ${xBase} ${yC - halfSpread * 0.55} L ${xBase + W * 0.1} ${yC} Z` },
      { kind: 'bleeder', d: `M ${xBase + W * 0.35} ${yC} L ${xBase} ${yC + halfSpread * 0.55} L ${xBase + W * 0.1} ${yC} Z` },
      ferruleSpine,
    ];
  }
  // fixed3 (default)
  return [sweptBlade(1, halfSpread), sweptBlade(-1, halfSpread), ferruleSpine];
}

// ── Nock classification + silhouettes ──────────────────────────────
// Modeled from real product photos (Sal, 2026-06). The nock sits at the REAR
// of the arrow; its U-throat opens LEFT (toward the string) and the body seats
// RIGHT into the shaft.

export type NockProfile =
  | 'pin'        // Easton 4mm/5mm press-in pin nock — smooth taper
  | 'g'          // Easton G nock — squared shoulder
  | 'beiter'     // Beiter pin nock — small precise barrel, parallel prongs
  | 'bohning'    // Bohning F/X/signature — wide-flared throat
  | 'accu'       // Gold Tip Accu-nock — bulged proprietary tail
  | 'launchpad'  // Carbon Express Launchpad — capture throat (in-curled tips)
  | 'lighted'    // Nockturnal / Lumenok — long drum body + LED
  | 'self';      // trad self-nock — groove cut in the shaft, no body

export function classifyNock(nock: Nock | undefined): NockProfile {
  if (!nock) return 'pin';
  const n = nock.name.toLowerCase();
  if (nock.lighted || /lighted|nockturnal|lumenok|burt coyote|illumin/.test(n)) return 'lighted';
  if (/beiter/.test(n)) return 'beiter';
  if (/launchpad|launch.?pad|capture/.test(n)) return 'launchpad';
  if (/accu/.test(n)) return 'accu';
  if (/self.?nock|\bself\b|trad/.test(n)) return 'self';
  if (/\bg nock\b|g pin|\bg\b/.test(n)) return 'g';
  if (/bohning|\bf nock\b|\bx nock\b|\bh nock\b|blazer/.test(n)) return 'bohning';
  return 'pin';
}

export interface NockShape {
  d: string;
  kind: 'body' | 'detail' | 'led' | 'slot';
}

interface NockParams {
  bodyLen: number; // barrel length (× h)
  prong: number;   // prong extension (× h)
  flare: number;   // prong flare out (× h)
  throatH: number; // half-height of the string slot (× h)
}

function nockBodyPath(xSeat: number, yC: number, h: number, p: NockParams): string {
  const xBack = xSeat - p.bodyLen * h;
  const xProng = xBack - p.prong * h;
  const xThroat = xBack - p.prong * 0.35 * h;
  const flare = p.flare * h;
  const tH = p.throatH * h;
  return `M ${xSeat} ${yC - h} L ${xBack} ${yC - h} L ${xProng} ${yC - h - flare} `
    + `L ${xThroat} ${yC - tH} L ${xThroat} ${yC + tH} L ${xProng} ${yC + h + flare} `
    + `L ${xBack} ${yC + h} L ${xSeat} ${yC + h} Z`;
}

/**
 * Nock silhouette drawn to the LEFT of `xSeat` (the shaft's rear face).
 * `h` is the shaft half-height (nock body roughly matches shaft OD).
 */
export function nockShapes(profile: NockProfile, xSeat: number, yC: number, h: number): NockShape[] {
  const H = Math.max(h, 3);

  if (profile === 'self') {
    // No nock body — a V string-groove cut into the shaft's rear face.
    return [{
      kind: 'slot',
      d: `M ${xSeat - 1} ${yC - H * 0.85} L ${xSeat + H * 1.7} ${yC} L ${xSeat - 1} ${yC + H * 0.85} Z`,
    }];
  }

  if (profile === 'lighted') {
    // Long cylindrical drum body + LED bulge near the rear.
    const p: NockParams = { bodyLen: 4.2, prong: 1.5, flare: 0.35, throatH: 0.5 };
    const xBack = xSeat - p.bodyLen * H;
    return [
      { kind: 'body', d: nockBodyPath(xSeat, yC, H * 1.05, p) },
      { kind: 'led', d: circlePath(xBack + H * 0.9, yC, H * 0.9) },
    ];
  }

  if (profile === 'launchpad') {
    // Capture throat — prong tips curl back inward over the slot.
    const p: NockParams = { bodyLen: 2.0, prong: 1.9, flare: 0.45, throatH: 0.4 };
    const xBack = xSeat - p.bodyLen * H;
    const xProng = xBack - p.prong * H;
    return [
      { kind: 'body', d: nockBodyPath(xSeat, yC, H, p) },
      { kind: 'detail', d: `M ${xProng} ${yC - H - 0.45 * H} L ${xProng + H * 0.5} ${yC - H * 0.55}` },
      { kind: 'detail', d: `M ${xProng} ${yC + H + 0.45 * H} L ${xProng + H * 0.5} ${yC + H * 0.55}` },
    ];
  }

  const params: Record<Exclude<NockProfile, 'self' | 'lighted' | 'launchpad'>, NockParams> = {
    pin: { bodyLen: 2.0, prong: 1.8, flare: 0.5, throatH: 0.5 },
    g: { bodyLen: 2.2, prong: 1.6, flare: 0.22, throatH: 0.5 },
    beiter: { bodyLen: 1.4, prong: 1.7, flare: 0.15, throatH: 0.45 },
    bohning: { bodyLen: 1.8, prong: 1.8, flare: 0.95, throatH: 0.55 },
    accu: { bodyLen: 2.3, prong: 1.7, flare: 0.5, throatH: 0.5 },
  };
  const p = params[profile as keyof typeof params];
  const shapes: NockShape[] = [{ kind: 'body', d: nockBodyPath(xSeat, yC, H, p) }];
  if (profile === 'g') {
    // squared shoulder step at the shaft seat
    shapes.push({ kind: 'detail', d: `M ${xSeat - H * 0.2} ${yC - H * 1.35} L ${xSeat - H * 0.2} ${yC + H * 1.35}` });
  }
  if (profile === 'accu') {
    // bulged tail mid-body
    shapes.push({ kind: 'detail', d: circlePath(xSeat - p.bodyLen * H * 0.55, yC, H * 1.25) });
  }
  return shapes;
}

function circlePath(cx: number, cy: number, r: number): string {
  return `M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0 Z`;
}
